import React from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { User, Bot, Code, ChevronDown, ChevronUp, BarChart3, TableIcon, Download, TrendingUp, TrendingDown, Minus, Lightbulb } from 'lucide-react';
import { cn } from '../lib/utils';
import DataChart from './DataChart';
import ColumnSelector from './ColumnSelector';
import UpgradePrompt from './UpgradePrompt';
import { ChartSuggestion, InsightResult } from '../types';
import { formatColumnName, formatCellValue } from '../utils/formatters';
import { exportToCsv } from '../utils/exportCsv';
import { queryAPI } from '../services/api';

interface ChatMessageProps {
  message: {
    type: 'user' | 'assistant';
    content: string;
    timestamp: Date;
    queryResponse?: {
      sql: string;
      columns: string[];
      allColumns?: string[];
      defaultVisibleColumns?: string[];
      columnAliases?: Record<string, string>;
      currencyColumns?: string[];
      results: any[];
      rowCount: number;
      totalRowCount?: number;
      isLimited?: boolean;
      explanation?: string;
      suggestedFollowUps?: string[];
      chartSuggestion?: ChartSuggestion;
      insights?: InsightResult;
    };
    isUpgradePrompt?: boolean;
    upgradeDetails?: { limit: number; phone: string };
  };
  onFollowUpClick?: (question: string) => void;
  originalQuery?: string; // The original natural language query
  conversationId?: string; // Conversation ID for CSV export
  hideSQL?: boolean; // Hide SQL-related UI (reasoning explanation, Show SQL button)
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, onFollowUpClick, originalQuery, conversationId, hideSQL }) => {
  const [showSQL, setShowSQL] = React.useState(false);
  const [showFullResults, setShowFullResults] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<'chart' | 'table'>('table');
  
  // Initialize visible columns from defaultVisibleColumns or columns
  const [visibleColumns, setVisibleColumns] = React.useState<string[]>(() => {
    if (message.queryResponse?.defaultVisibleColumns) {
      return message.queryResponse.defaultVisibleColumns;
    }
    return message.queryResponse?.columns || [];
  });

  const isUser = message.type === 'user';
  const queryResponse = message.queryResponse;
  
  // Use allColumns if available, otherwise fall back to columns
  const allColumns = queryResponse?.allColumns || queryResponse?.columns || [];
  
  // Filter results to only show visible columns
  const filterResultsByColumns = (results: any[]) => {
    return results.map(row => {
      const filteredRow: any = {};
      visibleColumns.forEach(col => {
        filteredRow[col] = row[col];
      });
      return filteredRow;
    });
  };
  
  const displayResults = showFullResults 
    ? filterResultsByColumns(queryResponse?.results || [])
    : filterResultsByColumns(queryResponse?.results?.slice(0, 5) || []);
  
  const hasChart = queryResponse?.chartSuggestion && queryResponse.chartSuggestion.type !== 'none';

  const handleColumnToggle = (column: string) => {
    setVisibleColumns(prev => 
      prev.includes(column)
        ? prev.filter(col => col !== column)
        : [...prev, column]
    );
  };

  const handleSelectAll = () => {
    setVisibleColumns(allColumns);
  };

  const handleDeselectAll = () => {
    setVisibleColumns([]);
  };

  const handleExportCsv = async () => {
    if (queryResponse) {
      console.log('Export CSV - rowCount:', queryResponse.rowCount, 'isLimited:', queryResponse.isLimited, 'originalQuery:', originalQuery);
      // If results were limited, use server-side export for full data
      if (queryResponse.isLimited && originalQuery) {
        try {
          console.log('Using server-side export for query:', originalQuery);
          await queryAPI.exportToCsv(originalQuery, conversationId);
        } catch (error) {
          console.error('Failed to export CSV:', error);
          // Fallback to client-side export
          const filteredResults = filterResultsByColumns(queryResponse.results);
          exportToCsv(
            filteredResults,
            visibleColumns,
            `query_results_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}`,
            queryResponse.columnAliases
          );
        }
      } else {
        // For 100 rows or less, use client-side export
        const filteredResults = filterResultsByColumns(queryResponse.results);
        exportToCsv(
          filteredResults,
          visibleColumns,
          `query_results_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}`,
          queryResponse.columnAliases
        );
      }
    }
  };

  return (
    <div className={cn("flex gap-3 mb-6", isUser ? "justify-end" : "justify-start")}>
      {!isUser && (
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
            <Bot className="w-4 h-4 text-primary-foreground" />
          </div>
        </div>
      )}

      <div className={cn("flex flex-col gap-2 max-w-[80%]", isUser && "items-end")}>
        {isUser ? (
          <Card className="bg-primary text-primary-foreground">
            <CardContent className="py-2 px-4">
              <p className="text-sm">{message.content}</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {message.isUpgradePrompt ? (
              <UpgradePrompt
                limit={message.upgradeDetails?.limit}
                phone={message.upgradeDetails?.phone}
              />
            ) : queryResponse ? (
              <div className="space-y-3">
                {!hideSQL && queryResponse.explanation && (
                  <Card>
                    <CardContent className="py-3 px-4">
                      <p className="text-sm">{queryResponse.explanation}</p>
                    </CardContent>
                  </Card>
                )}

                <Card>
                  <CardContent className="p-0">
                    <div className="p-4 border-b">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">
                            Query Results ({queryResponse.rowCount} rows)
                          </h4>
                          {queryResponse.isLimited && (
                            <p className="text-xs text-muted-foreground mt-1">
                              Result is limited to 100 rows{queryResponse.totalRowCount ? ` out of ${queryResponse.totalRowCount} total` : ''}, for full result download CSV
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          {hasChart && (
                            <div className="flex rounded-md shadow-sm">
                              <Button
                                variant={viewMode === 'chart' ? 'default' : 'outline'}
                                size="sm"
                                className="rounded-r-none"
                                onClick={() => setViewMode('chart')}
                              >
                                <BarChart3 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant={viewMode === 'table' ? 'default' : 'outline'}
                                size="sm"
                                className="rounded-l-none"
                                onClick={() => setViewMode('table')}
                              >
                                <TableIcon className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                          {!hideSQL && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowSQL(!showSQL)}
                            >
                              <Code className="w-4 h-4 mr-2" />
                              {showSQL ? 'Hide' : 'Show'} SQL
                            </Button>
                          )}
                          {viewMode === 'table' && (
                            <>
                              <ColumnSelector
                                allColumns={allColumns}
                                visibleColumns={visibleColumns}
                                columnAliases={queryResponse.columnAliases}
                                onColumnToggle={handleColumnToggle}
                                onSelectAll={handleSelectAll}
                                onDeselectAll={handleDeselectAll}
                              />
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleExportCsv}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Export CSV
                              </Button>
                            </>
                          )}
                        </div>
                      </div>

                      {showSQL && (
                        <div className="mt-3 p-3 bg-muted rounded-md">
                          <pre className="text-xs overflow-x-auto">
                            <code>{queryResponse.sql}</code>
                          </pre>
                        </div>
                      )}
                    </div>

                    {hasChart && viewMode === 'chart' ? (
                      <div className="p-4">
                        <DataChart 
                          data={queryResponse.results} 
                          suggestion={queryResponse.chartSuggestion!}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                          <Table>
                            <TableHeader className="sticky top-0 bg-background">
                              <TableRow>
                                {visibleColumns.map((column) => (
                                  <TableHead key={column} className="text-xs">
                                    {queryResponse.columnAliases?.[column] || formatColumnName(column)}
                                  </TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {displayResults?.map((row, index) => (
                                <TableRow key={index}>
                                  {visibleColumns.map((column) => (
                                    <TableCell key={column} className="text-xs">
                                      {formatCellValue(
                                        row[column], 
                                        column,
                                        queryResponse.currencyColumns?.includes(column) || false
                                      )}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        {queryResponse.results.length > 5 && viewMode === 'table' && (
                          <div className="p-2 border-t">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="w-full"
                              onClick={() => setShowFullResults(!showFullResults)}
                            >
                              {showFullResults ? (
                                <>
                                  <ChevronUp className="w-4 h-4 mr-2" />
                                  Show less
                                </>
                              ) : (
                                <>
                                  <ChevronDown className="w-4 h-4 mr-2" />
                                  Show all {queryResponse.rowCount} rows
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                      </>
                    )}
                  </CardContent>
                </Card>

                {queryResponse.insights?.narrative && (
                  <Card className="border-brand-stroke bg-brand-subtle/30">
                    <CardContent className="py-3 px-4">
                      <div className="flex items-start gap-2">
                        <Lightbulb className="w-4 h-4 text-brand mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-medium text-brand mb-1">Insight</p>
                          <p className="text-sm">{queryResponse.insights.narrative}</p>
                          {queryResponse.insights.comparisons.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2">
                              {queryResponse.insights.comparisons.slice(0, 3).map((comp, idx) => {
                                const Icon = comp.direction === 'up' ? TrendingUp : comp.direction === 'down' ? TrendingDown : Minus;
                                const colorClass = comp.direction === 'up' ? 'text-green-600' : comp.direction === 'down' ? 'text-red-600' : 'text-gray-500';
                                return (
                                  <Badge key={idx} variant="outline" className="text-xs py-1">
                                    <Icon className={cn("w-3 h-3 mr-1", colorClass)} />
                                    <span className={colorClass}>{comp.percentChange > 0 ? '+' : ''}{comp.percentChange}%</span>
                                    <span className="ml-1 text-muted-foreground">{comp.label}</span>
                                  </Badge>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {queryResponse.suggestedFollowUps && queryResponse.suggestedFollowUps.length > 0 && (
                  <Card>
                    <CardContent className="py-3 px-4">
                      <p className="text-sm font-medium mb-2">Suggested follow-up questions:</p>
                      <div className="flex flex-wrap gap-2">
                        {queryResponse.suggestedFollowUps.map((question, index) => (
                          <Badge
                            key={index}
                            variant="outline"
                            className="cursor-pointer rounded-full bg-brand-subtle border-brand-stroke text-brand hover:bg-brand-stroke transition-colors"
                            onClick={() => onFollowUpClick?.(question)}
                          >
                            {question}
                          </Badge>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <Card>
                <CardContent className="py-2 px-4">
                  <p className="text-sm">{message.content}</p>
                </CardContent>
              </Card>
            )}
          </>
        )}

        <p className="text-xs text-muted-foreground">
          {new Date(message.timestamp).toLocaleTimeString()}
        </p>
      </div>

      {isUser && (
        <div className="flex-shrink-0">
          <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
            <User className="w-4 h-4" />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatMessage;